import { ButtonType, ButtonWidget, CompactInlineAlertWidget, InlineAlertType } from '@tableau/widgets';
import * as clipboard from 'clipboard-polyfill';
import React from 'react';
import { match } from 'react-router-dom';

import { requestFailed } from '~src/bootstrap/redux/actions/RequestFailedAction';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { L10n } from '~src/commonInfra/L10n/L10n';
import { ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { IProjectInfo } from '~src/projects/models/IProjectInfo';
import { Projects } from '~src/projects/resources/Projects';
import { ActionsMenu } from '~src/spacesPlatform/components/ActionsMenu/ActionsMenu';

import { localizeConnectedApplicationDomainSafelistForDetailPage } from '../models/ConnectedApplicationInfo';
import { ConnectedApplicationInfoMapper } from '../models/ConnectedApplicationInfoMapper';
import { ConnectedApplications } from '../resources/ConnectedApplicationsResource';
import { CreateConnectedApplicationSecretResult } from '../serverApi/requests/CreateSecret';
import { callGetConnectedApplication } from '../serverApi/requests/GetConnectedApplication';
import { IConnectedApplicationSecretType, IConnectedApplicationType } from '../serverApi/types/ConnectedApplicationType';
import { ConnectedApplicationsDetailsPageStyles } from '../styles/ConnectedApplicationsDetailsPageStyles';
import { ConnectedApplicationBackButton } from './ConnectedApplicationBackButton';
import { SecretDisplayBox } from './SecretDisplayBox';


export type ConnectedApplicationDetailsSubspaceProps = { id: string };

// Weirdly just specifying the fact that our route type expects the ID to be defined is insufficient to actually have the ID in the type
// so we then need to specify that we do actually expect it in the "params" clause
export type ConnectedApplicationDetailsSubspaceRoutedProps = match<ConnectedApplicationDetailsSubspaceProps>;

export interface ConnectedApplicationDetailsSubspaceState {
  projectName?: string;
  connectedApplication: IConnectedApplicationType;
  accessScopeString?: string;
}

export class ConnectedApplicationDetailsSubspace extends React.Component<ConnectedApplicationDetailsSubspaceRoutedProps, ConnectedApplicationDetailsSubspaceState>
{
  public constructor(props: ConnectedApplicationDetailsSubspaceRoutedProps) {
    super(props);
    this.state = { connectedApplication: null };
    this.handleClick = this.handleClick.bind(this);
    this.rerenderCallback = this.rerenderCallback.bind(this);
  }

  async componentDidMount() {
    const connectedApplicationId = this.props.params.id;
    if (!this.state.connectedApplication) {
      const result = await callGetConnectedApplication(connectedApplicationId);
      this.setState({ connectedApplication: result.connectedApplication });
    }

    try {
      const projectName = await this.getProjectName();
      this.setState({ projectName: projectName });
      this.setState({ accessScopeString: this.getAccessScopeString() });
    } catch (error) {
      getAppStore().dispatch(requestFailed(error, Messages.errorUnknown()));
      this.setState({ projectName: Messages.connectedApplicationUnknownProject() });
    }
  }

  protected handleClick() {
    let currentSecrets: IConnectedApplicationSecretType[] = this.state.connectedApplication?.secret;
    if (currentSecrets === undefined) {
      currentSecrets = [];
    }
    ConnectedApplications.createSecret(this.props.params.id).then(
      (result: CreateConnectedApplicationSecretResult) => {
        currentSecrets.push(result.connectedApplicationSecret);
        let currentApp = this.state.connectedApplication;
        currentApp.secret = currentSecrets;
        this.setState({ connectedApplication: currentApp });
      });
  }

  public rerenderCallback(deletedSecretId: string) {
    let currentSecrets: IConnectedApplicationSecretType[] = this.state.connectedApplication?.secret;
    var index = currentSecrets.findIndex(x => x.id === deletedSecretId);
    if (index > -1) {
      currentSecrets.splice(index, 1);
    }
    let currentApp = this.state.connectedApplication;
    currentApp.secret = currentSecrets;
    this.setState({ connectedApplication: currentApp });
  }

  // Ideally we should just call Projects.getById(), but this API seems to take dbID instead of projectId
  // So we need to work with the relevant team to figure out how to pass in the correct id.
  // Currently, we just get all projects and find our project based on project luid match
  public async getProjectName(): Promise<string> {
    if ((FeatureFlags.connectedAppsMultiProjectEnabled && !this.state.connectedApplication?.projectIds) || !this.state.connectedApplication?.projectId) {
      return undefined;
    }
    const targetProjectId = FeatureFlags.connectedAppsMultiProjectEnabled && this.state.connectedApplication?.projectIds?.projectId.length === 1 ? this.state.connectedApplication.projectIds.projectId[0] : this.state.connectedApplication?.projectId;
    const projects: ResourceQuery<IProjectInfo> = Projects.all();
    let projectCount = await projects.getCount();
    let allProjects = (await projects.getSlice(0, projectCount)).items;
    let project = allProjects.find(p => p.luid === targetProjectId);
    return project?.name;
  }

  // get display string (All projects, Multiple projects, or `project_name` )  for access scope
  private getAccessScopeString(): string {
    if (FeatureFlags.connectedAppsMultiProjectEnabled) {
      if (!this.state.connectedApplication?.projectIds) {
        return Messages.connectedApplicationsScopeAllProjects();
      } else {
        const projectIds = this.state.connectedApplication.projectIds;
        return projectIds !== undefined && projectIds.projectId.length > 1 ? Messages.connectedApplicationsScopeMultipleProjects() : this.state.projectName;
      }
    } else {
      return Messages.connectedApplicationsScopeAllProjects();
    }
  }

  public render(): JSX.Element {
    // We should use the Info object to avoid in-place conversion in this class
    // https://tfs.tsi.lan/tfs/DefaultCollection/Tableau-Dev/_workitems/edit/1326979/
    const domains = this.state.connectedApplication != null ?
      localizeConnectedApplicationDomainSafelistForDetailPage((ConnectedApplicationInfoMapper.mapToDirectTrustInfoType([this.state.connectedApplication]))[0].domainSafelistForDisplay) :
      '';

    return <div style={{ display: 'flex', flexDirection: 'column' }}>
      <span className={ConnectedApplicationsDetailsPageStyles.backButtonSectionStyle}>
        <ConnectedApplicationBackButton />
      </span>
      <br></br>
      <span className={ConnectedApplicationsDetailsPageStyles.headerStyle} data-tb-test-id={ConnectedApplicationsDetailsPageStyles.connectedApplicationHeaderTestId}>
        <div style={{ paddingRight: 5 }}>{this.state.connectedApplication?.name}</div>
        <ActionsMenu
          targetItems={this.state.connectedApplication ? ConnectedApplicationInfoMapper.mapToDirectTrustInfoType([this.state.connectedApplication]) : []}
          disabled={!this.state.connectedApplication}
          useIcon={true}
        />
      </span>
      <br></br>
      <span className={ConnectedApplicationsDetailsPageStyles.subHeaderStyle} data-tb-test-id={ConnectedApplicationsDetailsPageStyles.connectedApplicationSubHeaderTestId}>
        {Messages.connectedApplicationsStatus()}
        <strong style={{ paddingLeft: 5, paddingRight: 10 }}>{this.state.connectedApplication?.enabled ? Messages.connectedApplicationsEnabled() : Messages.connectedApplicationsDisabled()}</strong>
        {Messages.created()}
        <strong style={{ paddingLeft: 5 }}>{this.state.connectedApplication?.createdAt && L10n.localizedDate(this.state.connectedApplication.createdAt)}</strong>
      </span>
      <table className={ConnectedApplicationsDetailsPageStyles.tableStyle}>
        <tbody>
          <tr>
            <td className={ConnectedApplicationsDetailsPageStyles.labelStyle}>
              {Messages.connectedApplicationsDetailsSecret()}
              <br></br><span className={ConnectedApplicationsDetailsPageStyles.cellDescriptionSubtextStyle}>{Messages.connectedApplicationsMaximumOf2()}</span>
            </td>
            <td className={ConnectedApplicationsDetailsPageStyles.secretsCell} data-tb-test-id={ConnectedApplicationsDetailsPageStyles.connectedApplicationSecretDetailsTestId}>
              {this.state.connectedApplication?.secret?.length >= 1 &&
                <SecretDisplayBox
                  appId={this.props.params.id}
                  secretId={this.state.connectedApplication.secret[0].id}
                  secretValue={this.state.connectedApplication.secret[0].value}
                  createdAt={this.state.connectedApplication.secret[0].createdAt}
                  rerenderCallback={this.rerenderCallback}
                />
              }
              {this.state.connectedApplication?.secret?.length === 2 &&
                <SecretDisplayBox
                  appId={this.props.params.id}
                  secretId={this.state.connectedApplication.secret[1].id}
                  secretValue={this.state.connectedApplication.secret[1].value}
                  createdAt={this.state.connectedApplication.secret[1].createdAt}
                  rerenderCallback={this.rerenderCallback}
                />
              }
              <div className={ConnectedApplicationsDetailsPageStyles.secretsCellLastRow}>
                {(this.state.connectedApplication?.secret === undefined || this.state.connectedApplication.secret.length < 2) &&
                  <ButtonWidget buttonType={ButtonType.Primary} disabled={false} onClick={this.handleClick}>{Messages.connectedApplicationsDetailsGenerateNewSecret()}</ButtonWidget>
                }
                {(this.state.connectedApplication?.secret === undefined || this.state.connectedApplication.secret.length === 0) &&
                  <div><em>{Messages.connectedApplicationsDetailsNoActiveSecret()}</em></div>
                }
                {this.state.connectedApplication?.secret?.length === 2 &&
                  <><ButtonWidget buttonType={ButtonType.Primary} disabled={true}>{Messages.connectedApplicationsDetailsGenerateNewSecret()}</ButtonWidget>
                    <div style={{ paddingRight: 15 }}>
                      <CompactInlineAlertWidget
                        children={Messages.connectedApplicationsDeleteASecret()}
                        style={{ display: 'inline-flex' }}
                        type={InlineAlertType.Info}
                        testId='delete-a-secret-info' />
                    </div></>
                }
              </div>
            </td>
          </tr>
          <tr>
            <td className={ConnectedApplicationsDetailsPageStyles.labelStyle}>
              {Messages.connectedApplicationsClientId()}
            </td>
            <td className={ConnectedApplicationsDetailsPageStyles.clientIdCell} data-tb-test-id={ConnectedApplicationsDetailsPageStyles.connectedApplicationClientIdDetailsTestId}>
              {this.state.connectedApplication?.clientId}
              <ButtonWidget buttonType={ButtonType.Outline} onClick={async () => await clipboard.writeText(this.state.connectedApplication.clientId)}>
                {Messages.connectedApplicationsDetailsCopyClientId()}
              </ButtonWidget>
            </td>
          </tr>
          <tr>
            <td className={ConnectedApplicationsDetailsPageStyles.labelStyle}>
              {Messages.connectedApplicationsAccess()}
            </td>
            <td className={ConnectedApplicationsDetailsPageStyles.detailsStyleWithBorder} data-tb-test-id={ConnectedApplicationsDetailsPageStyles.connectedApplicationAccessDetailsTestId}>
              {this.state.projectName ? this.state.projectName : this.state.accessScopeString}
            </td>
          </tr>
          <tr>
            <td className={ConnectedApplicationsDetailsPageStyles.labelStyle}>
              {Messages.connectedApplicationsDomainSafelist()}
            </td>
            <td className={ConnectedApplicationsDetailsPageStyles.detailsStyleWithBorder} data-tb-test-id={ConnectedApplicationsDetailsPageStyles.connectedApplicationDomainsDetailsTestId}>
              {domains}
            </td>
          </tr>
        </tbody>
      </table>
    </div>;
  }

}

